import { useEffect, useRef, useState } from "react";
import MainCard from "../../components/MainCard/MainCard";
import SearchIcon from "@mui/icons-material/Search";
import Table from "../../components/Table/Table";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {
  IMyLeadsCSVData,
  IMyLeadsParam,
  IRowMyClients,
  IRowMyLeads,
  IRowMyLeadsCoach,
} from "../../constants/interfaces";
import { DateTime } from "luxon";
import { useStore } from "../../store/StoreContext";
import { getSnapshot, Instance } from "mobx-state-tree";
import { ContextModel, SearchModel } from "../../models/Common";
import { useLocation, useNavigate } from "react-router-dom";
import { Column, columnTypes, TableOptions } from "../../components/DataTable/datatable.models";
import { Box, Link, Tab, Tabs } from "@mui/material";
import { ROUTE_PATHS } from "../../constants/routePaths";
import { RemoveRedEyeOutlined } from "@mui/icons-material";
import { LEAD_TYPES } from "../../constants/leadTypes";
import TabPanel from "../../components/Tabs/TabPanel";
import { PAGINATION } from "../../constants/const";

const MyCoachLeads = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { myLeadsStore } = useStore();
  const prevParams = location.state && location.state.keepParams ? getSnapshot(myLeadsStore.params) : undefined;

  // Commented at the moment, will back in next releases
  // const clientType: boolean = location.state?.clientType === "monthly";
  // if (clientType) {
  //   from = DateTime.now().startOf("month");
  //   to = from.endOf("month");
  //   startDate = from?.toISODate()?.toString();
  //   endDate = to?.toISODate()?.toString();
  // }
  const [param, setParam] = useState<IMyLeadsParam>({
    leadType: [LEAD_TYPES.COACH_LEAD_CLIENT, LEAD_TYPES.COACH_LEAD_NOT_CLIENT],
    startDate: prevParams ? prevParams.startDate : undefined,
    endDate: prevParams ? prevParams.endDate : undefined,
    lastName: prevParams ? prevParams.lastName : undefined,
    orderBy: prevParams?.orderBy,
    orderByDesc: prevParams?.orderBy ? undefined : prevParams?.orderByDesc,
    page: prevParams?.page ?? PAGINATION.page,
    size: prevParams?.size ?? PAGINATION.size,
  });
  const [context, setContext] = useState<Instance<typeof ContextModel> | null>();
  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<IRowMyLeads[] | []>([]);
  const [searchItems, setSearchItems] = useState<Instance<typeof SearchModel>[]>([]);
  const pageWasRendered = useRef(false);
  const [defaultSearch, setDefaultSearch] = useState<Instance<typeof SearchModel>>();
  const initialTab = location.pathname === ROUTE_PATHS.MY_COACH_LEADS ? 1 : 0;
  const [tab, setTab] = useState(initialTab);

  const columns: Column[] = [
    {
      name: "Date",
      value: "date",
      sortable: true,
      mobile: true,
    },
    {
      name: "First Name",
      value: "firstName",
      sortable: false,
      mobile: true,
    },
    {
      name: "Last Name",
      value: "lastName",
      sortable: true,
      mobile: true,
    },
    {
      name: "Phone",
      value: "phone",
      sortable: false,
      mobile: false,
    },
    {
      name: "Email",
      value: "email",
      sortable: false,
      mobile: false,
    },
    {
      name: "Client / Not a Client",
      value: "clientNotClient",
      sortable: false,
      mobile: false,
      minWidth: 25,
    },
    {
      name: "",
      value: "",
      type: columnTypes.CUSTOM,
      renderCell: (cell: any) => {
        const rowData = cell?.row?.original as IRowMyLeadsCoach;

        return rowData?.clientNotClient === "Client" ? (
          <RemoveRedEyeOutlined sx={{ fontSize: "2rem" }} className="icon-column" />
        ) : null;
      },
      mobile: true,
      align: "center",
    },
  ];

  const getMyLeads = async () => {
    try {
      setLoading(true);
      await myLeadsStore.fetch(param, false);
      setRows([...getSnapshot(myLeadsStore.rows)]);

      setContext(myLeadsStore.context);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const removeKeys = (keysToRemove: Array<keyof IMyLeadsParam>) => {
    const newObj = { ...param };

    keysToRemove.forEach((key) => {
      delete newObj[key];
    });

    return newObj;
  };

  const getMyLeadsSearch = async () => {
    try {
      setLoading(true);
      await myLeadsStore.fetchSearch(
        removeKeys(["page", "size", "lastName", "orderBy", "orderByDesc"]) as IMyLeadsParam
      );
      setSearchItems(myLeadsStore.searchItems);

      if (prevParams?.lastName) {
        const selectedUser = myLeadsStore.searchItems.find((item) => item.value === prevParams.lastName);
        setDefaultSearch(selectedUser);
      }
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * It will be fired every time pagination, sorting and filters are updated.
   * Here we want to avoid to applying useEffect upon the initial render.
   */
  useEffect(() => {
    if (pageWasRendered.current) {
      const fetchData = async () => {
        setLoading(true);
        try {
          await getMyLeads();
        } catch (error) {
          console.log(error);
        }
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param]);

  /**
   * On page load, get the data to fill the filters.
   */
  useEffect(() => {
    myLeadsStore.clear();
    const fetchData = async () => {
      setLoading(true);
      try {
        await getMyLeadsSearch();
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
    pageWasRendered.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pageWasRendered.current) {
      setLoading(false);
    }
  }, [rows]);

  const getOption = (option: any) => {
    setParam({ ...param, lastName: option?.value ?? undefined });
  };

  const getDate = (startDate: string | undefined, endDate: string | undefined) => {
    setParam({ ...param, startDate, endDate });
  };

  /**
   * It will be called every time the sorting or the pagination changed.
   */
  const onTableOptionsChanged = (tableParams: TableOptions) => {
    //save table pagination and sorting
    setParam({
      ...param,
      page: tableParams.page,
      size: tableParams.pageSize,
      orderBy: tableParams.sortBy,
      orderByDesc: tableParams.sortByDesc,
    });
  };

  const getDataForCSV = async (): Promise<IMyLeadsCSVData[]> => {
    const paramToCSV = { ...param, page: undefined, size: undefined, withoutPagination: true };
    const data = await myLeadsStore.fetchCSVData(paramToCSV);
    return data ?? [];
  };

  const handleNavigateClientLeads = () => {
    return navigate(ROUTE_PATHS.MY_CLIENT_LEADS);
  };

  const onRowClicked = (row: any) => {
    const isClient = row?.clientNotClient === "Client" || "";

    if (isClient)
      navigate(`${ROUTE_PATHS.MY_CLIENTS_TABS.replace(":id", (row as IRowMyClients)?.id.toString())}`, {
        state: { backLinkText: "Back", backLinkUrl: ROUTE_PATHS.MY_COACH_LEADS },
      });
  };

  const Header = (
    <Box sx={{ width: "100%", height: "100%", p: 1 }} className={`header`}>
      <b>Coach Leads:</b> Potential new Certified Coaches{" "}
    </Box>
  );

  const handleTabChange = (_: any, newValue: number) => {
    setTab(newValue);
    const path = newValue === 0 ? ROUTE_PATHS.MY_CLIENT_LEADS : ROUTE_PATHS.MY_COACH_LEADS;
    navigate(path);
  };

  return (
    <MainCard>
      <Box sx={{ px: 2, mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
          <Tab label="Client Leads" id="tab-client-leads" sx={{ textTransform: "capitalize", fontSize: "1.2rem" }} />
          <Tab label="Coach Leads" id="tab-coach-leads" sx={{ textTransform: "capitalize", fontSize: "1.2rem" }} />
        </Tabs>
      </Box>
      <TabPanel value={tab} index={1}></TabPanel>
      <Table
        idTable="coach-leads-table"
        getCSVData={getDataForCSV}
        idCalendar="coach-leads-input-calendar"
        header={Header}
        tableProps={{
          columns: columns,
          data: rows,
          loading: loading,
          paginationValue:
            prevParams && prevParams.page && prevParams.size
              ? {
                  page: prevParams.page - 1,
                  size: prevParams.size,
                }
              : undefined,
          totalRows: context?.totalRows ?? 0,
          ...(prevParams && prevParams.orderByDesc && { orderByDesc: prevParams.orderByDesc }),
          orderBy: prevParams && prevParams.orderBy ? prevParams.orderBy : param.orderBy,
          expandRowOnMobileBreakpoint: true,
          mobileBreakpoint: 900,
          handleTableOptionsChanged: onTableOptionsChanged,
          handleRowClicked: onRowClicked,
        }}
        autocompleteFilterProps={{
          singleValue: defaultSearch,
          withInputAutocompleteSingle: true,
          iconSingle: SearchIcon,
          optionsSingle: searchItems,
          labelSingle: "Search",
          placeholderSingle: "Last Name",
          sxSingle: { width: "24rem" },
          onChange: getOption,
          idSingle: "coach-leads-input-search",
        }}
        calendarProps={{
          withCalendarRange: true,
          sx: { maxWidth: "18rem" },
          // startDate: from,
          // endDate: to,
          startDate: prevParams?.startDate ? DateTime.fromISO(prevParams.startDate) : undefined,
          endDate: prevParams?.endDate ? DateTime.fromISO(prevParams.endDate) : undefined,
          customOnChange: getDate,
        }}
        downloadCSVButtonProps={{
          id: "coach-leads-btn-dowload",
          show: true,
          text: "Download",
          disabled: rows?.length === 0 ? true : false,
          startIcon: <SaveAltIcon />,
          variant: "outlined",
          size: "large",
        }}
      />
    </MainCard>
  );
};

export default MyCoachLeads;
